import React, { useState, useMemo } from "react";
import { AdminFunction, AdminEvent } from "./types";
import "./AdminEvent.css";

interface FunctionListProps {
    functions: AdminFunction[];
    events: AdminEvent[];
    onEdit: (func: AdminFunction) => void;
    onDelete: (functionID: number) => void;
    onCopy: (functionIds: number[], targetEventId: number) => void;
}

const FunctionList: React.FC<FunctionListProps> = ({
    functions,
    events,
    onEdit,
    onDelete,
    onCopy
}) => {
    const [search, setSearch] = useState("");
    const [selectedFunctions, setSelectedFunctions] = useState<number[]>([]);
    const [targetEventId, setTargetEventId] = useState<number | "">("");

    const filteredFunctions = useMemo(() => {
        return functions.filter((f) => {
            const searchLower = search.toLowerCase();

            return (
                f.functionName.toLowerCase().includes(searchLower) ||
                f.description?.toLowerCase().includes(searchLower) ||
                f.categoryName?.toLowerCase().includes(searchLower) ||
                f.assignedZones?.some((z) =>
                    z.zoneName.toLowerCase().includes(searchLower) ||
                    z.number.toString().includes(searchLower)
                )
            );
        });
    }, [functions, search]);

    const toggleFunction = (id: number) => {
        setSelectedFunctions((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    return (
        <div className="function-table-container">
            <div className="function-search">
                <input
                    type="text"
                    placeholder="Søg funktion, kategori, beskrivelse eller zone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* COPY TOOLBAR */}
            {selectedFunctions.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        marginBottom: "15px",
                        alignItems: "center",
                    }}
                >
                    <select
                        className="styled-dropdown"
                        style={{
                            flex: 1,
                            minWidth: 0,
                        }}
                        value={targetEventId}
                        onChange={(e) =>
                            setTargetEventId(
                                e.target.value ? Number(e.target.value) : ""
                            )
                        }
                    >
                        <option value="">-- Vælg Event --</option>

                        {events.map((ev) => (
                            <option key={ev.eventID} value={ev.eventID}>
                                {ev.eventName}
                            </option>
                        ))}
                    </select>

                    <button
                        className="add-btnnew"
                        style={{
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            padding: "0 18px",
                            minWidth: "170px",
                            height: "42px", // samme højde som dropdown
                        }}
                        disabled={!targetEventId}
                        onClick={() => {
                            onCopy(selectedFunctions, Number(targetEventId));

                            setSelectedFunctions([]);
                            setTargetEventId("");
                        }}
                    >
                        Kopiér valgte ({selectedFunctions.length})
                    </button>
                </div>
            )}

            <table className="event-table event-table-modern">
                <thead>
                    <tr>
                        <th></th>
                        <th>ID</th>
                        <th>Funktion Navn</th>
                        <th>Kategori</th>
                        <th>Beskrivelse</th>
                        <th>Kortdesign</th>
                        <th>Zones</th>
                        <th>Handlinger</th>
                    </tr>
                </thead>

                <tbody>
                    {filteredFunctions.map((f) => (
                        <tr key={f.functionID}>
                            <td>
                                <input
                                    type="checkbox"
                                    checked={selectedFunctions.includes(f.functionID!)}
                                    onChange={() => toggleFunction(f.functionID!)}
                                />
                            </td>

                            <td>{f.functionID}</td>
                            <td>{f.functionName}</td>
                            <td>{f.categoryName || "-"}</td>
                            <td>{f.description || "-"}</td>
                            <td>{f.useDesign2 ? "Design 2" : "Design 1"}</td>

                            <td>
                                {f.assignedZones?.length ? (
                                    <div className="zone-chip-container">
                                        {f.assignedZones.map((z) => (
                                            <div key={z.zoneID} className="zone-chip">
                                                <strong>{z.number}</strong> -{" "}
                                                <span className="zone-chip-name">
                                                    {z.zoneName}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="no-zones">
                                        Ingen zones
                                    </span>
                                )}
                            </td>

                            <td>
                                <div className="table-buttons">
                                    <button
                                        className="edit-btn"
                                        onClick={() => onEdit(f)}
                                    >
                                        Rediger
                                    </button>

                                    <button
                                        className="delete-btn"
                                        onClick={() => onDelete(f.functionID!)}
                                    >
                                        Slet
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default FunctionList;